import i18n, { type BackendModule, type ReadCallback } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { initReactI18next } from 'react-i18next'
import { AppLocale } from './locales'
import { TRANS_DEFAULT_PROPS } from './transDefaults'

// The app's four namespaces. All of them are preloaded for the ACTIVE language
// only (see `ns` below) so direct `i18n.t('…', { ns: 'admin' })` calls scattered
// across entities resolve synchronously — while other languages stay deferred.
const NAMESPACES = ['common', 'admin', 'participant', 'representative'] as const

// Minimal i18next backend that fetches each (language, namespace) JSON from the
// statically-served `/locales` directory on demand — so a user only ever loads
// the language they're viewing, and switching language fetches the new one
// lazily. The files live in `public/locales` (deployed as-is), so this needs no
// bundling and no extra dependency. Fetching beats bundling-from-public here: a
// non-eager glob over `public/` makes Vite both copy the JSON to `dist/locales`
// AND emit duplicate JS chunks for it.
//
// `?v=` is a hash of the translation files (`localesVersion` in vite.config.ts):
// new translations get a new URL, so no browser cache can pair them with old code.
const lazyHttpBackend: BackendModule = {
  type: 'backend',
  init: () => {},
  read(lng: string, ns: string, callback: ReadCallback) {
    fetch(`/locales/${lng}/${ns}.json?v=${__LOCALES_VERSION__}`).then(
      async (res) => {
        if (!res.ok) {
          callback(new Error(`Missing locale resource: ${lng}/${ns} (${res.status})`), false)
          return
        }
        callback(null, (await res.json()) as Record<string, string>)
      },
      (err: unknown) => callback(err as Error, false)
    )
  },
}

i18n
  .use(lazyHttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: AppLocale.ru,
    supportedLngs: Object.values(AppLocale),
    ns: NAMESPACES,
    defaultNS: 'common',
    fallbackNS: 'common',
    // Resources arrive via the backend on demand rather than a prebuilt bundle.
    partialBundledLanguages: true,

    detection: {
      // Check localStorage first, then browser language
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },

    // Map browser language codes to our supported codes (kk → kz)
    load: 'languageOnly',

    interpolation: {
      escapeValue: false,
    },

    react: { transDefaultProps: TRANS_DEFAULT_PROPS },
  })

export default i18n
